import { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useFormValidation from '../../hooks/useFormValidation';
import {
  createAdminSchema,
  updateAdminSchema,
  ICreateAdminFormData,
} from '../../schemas/adminSchemas';
import {
  useCreateAdmin,
  useUpdateAdmin,
} from '../../hooks/queries/useAdminQueries';
import {
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
  getPhonePlaceholder,
  validatePhoneByCountry,
} from '../../constants/countryCodes';
import { IBackofficeAdmin } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const PERMISSION_KEYS = [
  {
    key: 'users',
    labelKey: 'permissions.users',
    descKey: 'permissions.users_desc',
  },
  {
    key: 'spaces',
    labelKey: 'permissions.spaces',
    descKey: 'permissions.spaces_desc',
  },
  {
    key: 'feedback',
    labelKey: 'permissions.feedback',
    descKey: 'permissions.feedback_desc',
  },
  {
    key: 'reports',
    labelKey: 'permissions.reports',
    descKey: 'permissions.reports_desc',
  },
  {
    key: 'audit_logs',
    labelKey: 'permissions.audit_logs',
    descKey: 'permissions.audit_logs_desc',
  },
  {
    key: 'settings',
    labelKey: 'permissions.settings',
    descKey: 'permissions.settings_desc',
  },
  {
    key: 'admins',
    labelKey: 'permissions.admins',
    descKey: 'permissions.admins_desc',
  },
];

const INITIAL_VALUES: ICreateAdminFormData = {
  username: '',
  displayName: '',
  password: '',
  email: '',
};

interface IAdminFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  admin?: IBackofficeAdmin | null;
}

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-xs text-red-500 mt-1">{message}</p> : null;

const AdminFormDialog = ({ isOpen, onClose, admin }: IAdminFormDialogProps) => {
  const { t } = useTranslation();
  const isEdit = !!admin;
  const schema = isEdit ? updateAdminSchema : createAdminSchema;
  const { values, errors, handleChange, handleSubmit, reset, isSubmitting } =
    useFormValidation(schema as any, INITIAL_VALUES);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const createAdmin = useCreateAdmin();
  const updateAdmin = useUpdateAdmin();

  useEffect(() => {
    if (isOpen && admin) {
      reset({
        username: admin.username,
        displayName: admin.displayName,
        password: '',
        email: admin.email || '',
      });
      setPermissions(admin.permissions ?? []);
      setPermissionError(null);
      setCountryCode(admin.countryCode || DEFAULT_COUNTRY_CODE);
      setMobileNumber(admin.mobileNumber || '');
    } else if (isOpen) {
      reset();
      setPermissions([]);
      setPermissionError(null);
      setMobileError(null);
      setCountryCode(DEFAULT_COUNTRY_CODE);
      setMobileNumber('');
    }
  }, [isOpen, admin]);

  const handleTogglePermission = (key: string) => {
    setPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
    setPermissionError(null);
  };

  const handleFormSubmit = async () => {
    if (!isEdit && permissions.length === 0) {
      setPermissionError(t('validation.permissions_required'));
      return;
    }

    if (mobileNumber && !validatePhoneByCountry(countryCode, mobileNumber)) {
      setMobileError(t('validation.mobile_invalid_for_country'));
      return;
    }

    setPermissionError(null);
    setMobileError(null);

    await handleSubmit(async (data) => {
      if (isEdit) {
        await updateAdmin.mutateAsync({
          id: admin!.id,
          data: {
            displayName: data.displayName,
            email: data.email?.trim() || null,
            password: data.password || undefined,
            countryCode: countryCode || undefined,
            mobileNumber: mobileNumber || undefined,
            permissions,
          },
        });
      } else {
        await createAdmin.mutateAsync({
          username: data.username,
          displayName: data.displayName,
          password: data.password,
          email: data.email?.trim() || undefined,
          countryCode: mobileNumber ? countryCode : undefined,
          mobileNumber: mobileNumber || undefined,
          permissions,
        });
      }
      onClose();
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('admin.edit_title', { name: admin?.username ?? '' })
              : t('admin.create_title')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('common.username')}
            </label>
            <Input
              value={values.username}
              onChange={(e) => handleChange('username', e.target.value)}
              disabled={isEdit}
              placeholder="admin001"
            />
            <FieldError message={errors.username} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('common.display_name')}
            </label>
            <Input
              value={values.displayName}
              onChange={(e) => handleChange('displayName', e.target.value)}
              placeholder={t('common.display_name')}
            />
            <FieldError message={errors.displayName} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('common.email')}
            </label>
            <Input
              type="email"
              value={values.email ?? ''}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="admin@example.com"
            />
            <FieldError message={errors.email} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('account.mobile')}
            </label>
            <div className="flex gap-2">
              <Select
                value={countryCode}
                onValueChange={(value) => setCountryCode(value)}
              >
                <SelectTrigger className="w-auto min-w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map(({ code, label: countryCodeLabel }) => (
                    <SelectItem key={code} value={code}>
                      {countryCodeLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="text"
                value={mobileNumber}
                onChange={(e) => {
                  setMobileNumber(e.target.value);
                  setMobileError(null);
                }}
                placeholder={getPhonePlaceholder(countryCode)}
                className="flex-1"
              />
            </div>
            <FieldError message={mobileError ?? undefined} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {isEdit ? t('admin.new_password') : t('common.password')}
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={t('admin.min_9_chars')}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            {!isEdit && (
              <p className="text-xs text-muted-foreground mt-1">
                {t('validation.password_hint')}
              </p>
            )}
            <FieldError message={errors.password} />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('permissions.title')}
            </label>
            <div className="border border-border rounded-lg p-4 space-y-3">
              {PERMISSION_KEYS.map(({ key, labelKey, descKey }) => (
                <label
                  key={key}
                  className="flex items-start gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={permissions.includes(key)}
                    onChange={() => handleTogglePermission(key)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-black focus:ring-gray-500"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {t(labelKey)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t(descKey)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <FieldError message={permissionError ?? undefined} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleFormSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? t('common.saving')
              : isEdit
                ? t('common.update')
                : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminFormDialog;
